/**
 * MCP Tool Provider 管理 API（对齐 Dify 工作流 MCP 语义）
 *
 * 对应后端: apps/agent-service/src/platform/api/v1/tool_provider_mcp.py
 * 前缀: /api/v1/tools/mcp-providers
 *
 * 与 `api/mcp.ts` 的区别：
 * - `api/mcp.ts` 管 CPID 下的运维态 MCP 服务（mcp_services 表）。
 * - 本文件管 Dify Agent 节点引用的 MCP Provider（mcp_tool_providers 表，
 *   按 project_id + server_identifier 路由，支持 OAuth 2.1）。
 */

import apiClient from './index'

// ============== 类型定义 ==============

export interface MCPProviderConfiguration {
  timeout: string
  sse_read_timeout: string
}

export interface MCPProviderTool {
  name: string
  description?: string
  inputSchema?: Record<string, unknown>
}

export interface MCPToolProvider {
  id: string
  name: string
  project_id: string
  user_id: string
  server_identifier: string
  server_url: string // masked
  raw_server_url?: string | null
  icon: string
  is_team_authorization: boolean
  tools: MCPProviderTool[]
  configuration: MCPProviderConfiguration
  masked_headers: Record<string, string>
  headers?: Record<string, string>
  authentication?: Record<string, string>
  created_at: number
  updated_at: number
}

export interface MCPProviderCreate {
  name: string
  server_url: string
  server_identifier?: string
  icon?: string
  headers?: Record<string, string>
  timeout?: number
  sse_read_timeout?: number
  /** 创建后是否立即探测 tools/list（headers 可用时直接认领） */
  probe?: boolean
}

export type MCPProviderUpdate = Partial<
  Omit<MCPProviderCreate, 'server_identifier' | 'probe'>
>

export interface StartOAuthRequest {
  redirect_uri: string
  scope?: string
}

export interface StartOAuthResponse {
  authorize_url: string
  state: string
}

export interface ReconnectProbeResponse {
  authed: boolean
  tools: MCPProviderTool[]
  error?: string | null
}

// ============== API 方法 ==============

const BASE_PATH = '/tools/mcp-providers'

export const mcpToolProviderApi = {
  /** 列出当前项目的全部 MCP Provider */
  async list(): Promise<MCPToolProvider[]> {
    return apiClient.get<MCPToolProvider[]>(BASE_PATH)
  },

  /** 创建 MCP Provider（可选：`probe=true` 立即探测） */
  async create(data: MCPProviderCreate): Promise<MCPToolProvider> {
    return apiClient.post<MCPToolProvider>(BASE_PATH, data)
  },

  /** 更新 Provider（URL 变更后 authed / tools 会被清空） */
  async update(
    providerId: string,
    data: MCPProviderUpdate
  ): Promise<MCPToolProvider> {
    return apiClient.put<MCPToolProvider>(`${BASE_PATH}/${providerId}`, data)
  },

  /** 删除 Provider */
  async remove(providerId: string): Promise<{ success: boolean }> {
    return apiClient.delete<{ success: boolean }>(`${BASE_PATH}/${providerId}`)
  },

  /** 强制刷新 tools/list 缓存 */
  async refreshTools(providerId: string): Promise<MCPToolProvider> {
    return apiClient.post<MCPToolProvider>(
      `${BASE_PATH}/${providerId}/refresh-tools`
    )
  },

  /** 用当前配置做一次临时连通性测试（不写库） */
  async testConnection(providerId: string): Promise<ReconnectProbeResponse> {
    return apiClient.post<ReconnectProbeResponse>(
      `${BASE_PATH}/${providerId}/test-connection`
    )
  },

  /** 启动 OAuth 授权，拿到浏览器要打开的 URL */
  async startOAuth(
    providerId: string,
    body: StartOAuthRequest
  ): Promise<StartOAuthResponse> {
    return apiClient.post<StartOAuthResponse>(
      `${BASE_PATH}/${providerId}/oauth/start`,
      body
    )
  }
}

export default mcpToolProviderApi
